import React, { useCallback, useId, useState } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Input,
  Switch,
} from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import type { ChatAgentMetadata } from "@/types/backend-contracts"

import { TEXT_SIMULATOR_TOOLTIPS } from "./textSimulatorTooltips"

interface TextSimulatorAdvancedSettingsProps {
  metadata: Partial<ChatAgentMetadata>
  onMetadataUpdate: (metadata: Partial<ChatAgentMetadata>) => void
  disabled: boolean
}

const TEMPLATE_EXAMPLES = {
  tool_call_extraction: `{
  "enabled": true,
  "tool_calls_path": "steps[].toolCalls[]",
  "tool_call_filter_field": "type",
  "tool_call_filter_value": "tool-call",
  "tool_call_mappings": {
    "id": "payload.toolCallId",
    "name": "payload.toolName",
    "arguments": "payload.args"
  }
}`,
  role_mapping: `{
  "assistant": "agent"
}`,
  input_template: `{
  "user_id": "{{custom_data.user_id}}",
  "session_id": "{{session_id}}",
  "input_text": "{{latest_message}}"
}`,
}

const formatJsonString = (value: string | object | null | undefined) => {
  if (!value) {
    return ""
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2)
  }
  if (value.trim() === "") {
    return ""
  }

  try {
    const parsed: unknown = JSON.parse(value)
    return JSON.stringify(parsed, null, 2)
  } catch {
    return value
  }
}

export const TextSimulatorAdvancedSettings: React.FC<
  TextSimulatorAdvancedSettingsProps
> = ({ metadata, onMetadataUpdate, disabled }) => {
  const [templateError, setTemplateError] = useState<string | null>(null)
  const [toolCallConfigError, setToolCallConfigError] = useState<string | null>(
    null,
  )
  const [roleMappingError, setRoleMappingError] = useState<string | null>(null)

  const validateAndUpdateJson = useCallback(
    (
      fieldName: string,
      value: string,
      setError: (error: string | null) => void,
    ) => {
      if (value.trim() === "") {
        setError(null)
      } else {
        try {
          JSON.parse(value)
          setError(null)
        } catch {
          setError("Invalid JSON format - saved as typed")
        }
      }
      onMetadataUpdate({ [fieldName]: value })
    },
    [onMetadataUpdate],
  )

  const validateAndUpdateTemplate = useCallback(
    (value: string) => {
      if (value.trim() === "") {
        setTemplateError(null)
        onMetadataUpdate({ input_template: null })
        return
      }

      try {
        const withPlaceholders = value
          .replace(/"\{\{[^}]+\}\}"/g, '"PLACEHOLDER"')
          .replace(/\{\{[^}]+\}\}/g, '"PLACEHOLDER"')
        JSON.parse(withPlaceholders)
        setTemplateError(null)
      } catch {
        setTemplateError("Invalid JSON template - saved as typed")
      }

      onMetadataUpdate({ input_template: value.trim() || null })
    },
    [onMetadataUpdate],
  )

  const formattedValues = {
    tool_call_extraction: formatJsonString(metadata.tool_call_extraction),
    role_mapping: formatJsonString(metadata.role_mapping),
  }

  const inputTemplateLabelId = useId()
  const responseMsgPathLabelId = useId()
  const responseMsgPathInputId = useId()
  const stripTimestampsId = useId()
  const stripTimestampsLabelId = useId()
  const toolCallLabelId = useId()
  const roleMappingLabelId = useId()

  return (
    <Accordion type="single" collapsible className="px-0">
      <AccordionItem value="api-template">
        <AccordionTrigger>
          <div className="flex flex-col items-start gap-0.5 text-left">
            <span>API Template Configuration (Advanced)</span>
            <span className="text-xs text-coval-text-muted font-normal">
              Configure custom API input templates and response parsing
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="space-y-4 pt-2">
            {/* Input Template */}
            <div className="space-y-2">
              <FieldLabel
                id={inputTemplateLabelId}
                tooltip={TEXT_SIMULATOR_TOOLTIPS.inputTemplate}
              >
                Input Template (JSON with Variables)
              </FieldLabel>
              <JsonCodeEditor
                placeholder={TEMPLATE_EXAMPLES.input_template}
                value={metadata.input_template || ""}
                onChange={validateAndUpdateTemplate}
                rows={5}
                disabled={disabled}
                error={templateError}
                // `{{variables}}` may stand in for whole values here, which a
                // plain parse rejects; validateAndUpdateTemplate substitutes
                // them first and owns the verdict.
                lint={false}
                ariaLabelledBy={inputTemplateLabelId}
              />
              <p className="text-xs text-coval-text-muted">
                JSON template for API input. Available variables: {"{"}
                {"{"}messages{"}"} - Full conversation history, {"{"}
                {"{"}latest_message{"}"} - Latest user message, {"{"}
                {"{"}session_id{"}"} - Session ID, {"{"}
                {"{"}custom_data.FIELD{"}"} - Custom data fields, {"{"}
                {"{"}any.nested.path{"}"} - Any field from init response (use
                exact dot notation)
              </p>
            </div>

            {/* Response Message Path */}
            <div className="space-y-2">
              <FieldLabel
                id={responseMsgPathLabelId}
                htmlFor={responseMsgPathInputId}
                tooltip={TEXT_SIMULATOR_TOOLTIPS.responseMessagePath}
              >
                Response Message Path
              </FieldLabel>
              <Input
                id={responseMsgPathInputId}
                placeholder="output_message"
                value={metadata.response_message_path || ""}
                onChange={(e) =>
                  onMetadataUpdate({
                    response_message_path: e.target.value.trim() || null,
                  })
                }
                disabled={disabled}
              />
              <p className="text-xs text-coval-text-muted">
                Dot notation path to extract message from API response.
                Examples: &ldquo;output_message&rdquo;,
                &ldquo;choices.0.message.content&rdquo;
              </p>
            </div>

            {/* Strip Message Timestamps */}
            <div className="flex items-start gap-2">
              <Switch
                id={stripTimestampsId}
                checked={metadata.strip_message_timestamps || false}
                onCheckedChange={(checked) =>
                  onMetadataUpdate({ strip_message_timestamps: checked })
                }
                disabled={disabled}
                aria-labelledby={stripTimestampsLabelId}
              />
              <div className="flex flex-col gap-1">
                <label
                  htmlFor={stripTimestampsId}
                  id={stripTimestampsLabelId}
                  className="text-sm text-coval-text cursor-pointer"
                >
                  Strip Message Timestamps
                </label>
                <span className="text-xs text-coval-text-muted">
                  Remove timestamp fields from messages before sending to your
                  API. Enable if your API rejects messages containing timestamp
                  properties.
                </span>
              </div>
            </div>

            {/* Tool Call Extraction Config */}
            <div className="space-y-2">
              <FieldLabel
                id={toolCallLabelId}
                tooltip={TEXT_SIMULATOR_TOOLTIPS.toolCallExtraction}
              >
                Tool Call Extraction Config
              </FieldLabel>
              <JsonCodeEditor
                placeholder={
                  formattedValues.tool_call_extraction ||
                  TEMPLATE_EXAMPLES.tool_call_extraction
                }
                value={formattedValues.tool_call_extraction}
                onChange={(value) =>
                  validateAndUpdateJson(
                    "tool_call_extraction",
                    value,
                    setToolCallConfigError,
                  )
                }
                rows={8}
                disabled={disabled}
                error={toolCallConfigError}
                ariaLabelledBy={toolCallLabelId}
              />
              <p className="text-xs text-coval-text-muted">
                Configure how to extract tool/function calls from your custom
                API responses. Leave empty if your API returns OpenAI-compatible
                format or doesn&apos;t use tool calls. Use path notation like
                &ldquo;steps[].toolCalls[]&rdquo; for arrays.
              </p>
            </div>

            {/* Role Mapping */}
            <div className="space-y-2">
              <FieldLabel
                id={roleMappingLabelId}
                tooltip={TEXT_SIMULATOR_TOOLTIPS.roleMapping}
              >
                Role Mapping (Optional)
              </FieldLabel>
              <JsonCodeEditor
                placeholder={
                  formattedValues.role_mapping || TEMPLATE_EXAMPLES.role_mapping
                }
                value={formattedValues.role_mapping}
                onChange={(value) =>
                  validateAndUpdateJson(
                    "role_mapping",
                    value,
                    setRoleMappingError,
                  )
                }
                rows={4}
                disabled={disabled}
                error={roleMappingError}
                ariaLabelledBy={roleMappingLabelId}
              />
              <div className="text-xs text-coval-text-muted space-y-1">
                <p>
                  JSON object mapping internal role names to API-expected role
                  names.
                </p>
                <p className="font-medium">Common mappings:</p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li>
                    <code className="bg-surface-1 px-1 rounded">
                      &ldquo;assistant&rdquo;: &ldquo;agent&rdquo;
                    </code>{" "}
                    - For APIs that use &ldquo;agent&rdquo; role
                  </li>
                  <li>
                    <code className="bg-surface-1 px-1 rounded">
                      &ldquo;user&rdquo;: &ldquo;customer&rdquo;
                    </code>{" "}
                    - For APIs that use &ldquo;customer&rdquo; role
                  </li>
                  <li>
                    <code className="bg-surface-1 px-1 rounded">
                      &ldquo;system&rdquo;: &ldquo;instructions&rdquo;
                    </code>{" "}
                    - For APIs with custom system role
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
